import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './AdminQuickActions.css';

const AdminQuickActions = ({
  showAnalytics,
  setShowAnalytics,
  setShowAuditLogModal,
  setShowReportModal,
  setShowCommunicationModal,
  setShowScheduleModal,
  setShowConflictModal,
  setShowWaitlistModal,
  setShowFeedbackModal,
  setShowMaintenanceModal,
  setShowBlackoutModal,
  setShowPricingModal,
  setShowPermissionsModal,
  setShowAnnouncementModal,
  detectConflicts,
  selectedBookings,
  setShowBulkActionModal,
  setShowSMSModal,
  setShowWhatsAppModal,
  setShowEmailModal,
  conflictsCount,
  waitlistCount,
  feedbacksCount,
  maintenanceCount,
  blackoutCount,
  pricingCount,
  announcementsCount
}) => {
  // Action categories for better organization
  const actionCategories = [
    {
      title: 'Analysis',
      actions: [
        { id: 'analytics', icon: '📊', label: 'Analytics', onClick: () => setShowAnalytics(!showAnalytics), active: showAnalytics },
        { id: 'audit', icon: '📋', label: 'Audit Log', onClick: () => setShowAuditLogModal(true) },
        { id: 'reports', icon: '📑', label: 'Reports', onClick: () => setShowReportModal(true) },
      ]
    },
    {
      title: 'Management',
      actions: [
        { id: 'schedule', icon: '📅', label: 'Schedule', onClick: () => setShowScheduleModal(true) },
        { id: 'conflicts', icon: '⚠️', label: 'Conflicts', count: conflictsCount, onClick: detectConflicts },
        { id: 'waitlist', icon: '⏳', label: 'Waitlist', count: waitlistCount, onClick: () => setShowWaitlistModal(true) },
        { id: 'maintenance', icon: '🔧', label: 'Maintenance', count: maintenanceCount, onClick: () => setShowMaintenanceModal(true) },
      ]
    },
    {
      title: 'Settings',
      actions: [
        { id: 'blackout', icon: '🚫', label: 'Blackout', count: blackoutCount, onClick: () => setShowBlackoutModal(true) },
        { id: 'pricing', icon: '💰', label: 'Pricing', count: pricingCount, onClick: () => setShowPricingModal(true) },
        { id: 'permissions', icon: '🔒', label: 'Permissions', onClick: () => setShowPermissionsModal(true) },
        { id: 'announce', icon: '📢', label: 'Announce', count: announcementsCount, onClick: () => setShowAnnouncementModal(true) },
      ]
    },
    {
      title: 'Communication',
      actions: [
        { id: 'feedback', icon: '⭐', label: 'Feedback', count: feedbacksCount, onClick: () => setShowFeedbackModal(true) },
        { id: 'communication', icon: '💬', label: 'Messages', onClick: () => setShowCommunicationModal(true) },
      ]
    }
  ];

  return (
    <div className="admin-quick-actions-3d">
      {/* Header with 3D effect */}
      <motion.div 
        className="quick-actions-header-3d"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="header-left">
          <div className="header-icon-3d">
            <span className="header-emoji">⚡</span>
          </div>
          <div className="header-text">
            <h2>Quick Actions</h2>
            <span className="header-subtitle">Administrative Controls</span>
          </div>
        </div>
        
        {/* Bulk Actions - Only show when items selected */}
        <AnimatePresence>
          {selectedBookings.length > 0 && (
            <motion.div 
              className="bulk-actions-3d"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <div className="selected-info">
                <span className="selected-badge">{selectedBookings.length}</span>
                <span className="selected-text">selected</span>
              </div>
              
              <div className="bulk-buttons">
                <motion.button 
                  className="bulk-btn-3d primary"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setShowBulkActionModal(true)}
                >
                  <span className="btn-icon">📦</span>
                  <span>Bulk</span>
                </motion.button>
                
                <motion.button 
                  className="bulk-btn-3d sms"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setShowSMSModal(true)}
                >
                  <span className="btn-icon">📱</span>
                  <span>SMS</span>
                </motion.button>
                
                <motion.button 
                  className="bulk-btn-3d whatsapp"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setShowWhatsAppModal(true)}
                >
                  <span className="btn-icon">💬</span>
                  <span>WA</span>
                </motion.button>
                
                <motion.button 
                  className="bulk-btn-3d email"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setShowEmailModal(true)}
                >
                  <span className="btn-icon">✉️</span>
                  <span>Email</span>
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Quick Actions Grid by Category */}
      <div className="actions-container-3d">
        {actionCategories.map((category, idx) => (
          <motion.div 
            key={category.title}
            className="action-category-3d"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
          >
            <h3 className="category-title-3d">
              <span className="category-line"></span>
              {category.title}
            </h3>
            
            <div className="category-grid-3d">
              {category.actions.map((action) => (
                <motion.button
                  key={action.id}
                  className={`action-card-3d ${action.active ? 'active' : ''} ${action.count > 0 ? 'has-count' : ''}`}
                  onClick={action.onClick}
                  whileHover={{ y: -4, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="card-content">
                    <div className="card-icon-3d">
                      <span className="card-emoji">{action.icon}</span>
                      {action.count > 0 && (
                        <motion.span 
                          className="count-badge-3d"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 500 }}
                        >
                          {action.count}
                        </motion.span>
                      )}
                    </div>
                    <span className="card-label-3d">{action.label}</span>
                  </div>
                  
                  {/* 3D effect overlay */}
                  <div className="card-shine"></div>
                </motion.button>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Floating decorative elements */}
      <div className="floating-elements">
        <div className="floating-dot dot-1"></div>
        <div className="floating-dot dot-2"></div>
        <div className="floating-dot dot-3"></div>
      </div>
    </div>
  );
};

export default AdminQuickActions;